import React from 'react';
import ReactDOM from 'react-dom/client';
import { ClerkProvider } from '@clerk/clerk-react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import { App } from './App';
import './index.css';
import { CopilotProvider } from './lib/copilot-context';

const publishableKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY as string | undefined;
if (!publishableKey) {
  throw new Error('VITE_CLERK_PUBLISHABLE_KEY is required');
}

// Sentry reports from PRODUCTION BUILDS only. The local .env carries the
// production DSN, so the Vite dev server used to report a laptop's errors into
// the same project as real users'. VITE_SENTRY_ALLOW_NON_PRODUCTION=true opts a
// dev build in deliberately. Mirrors server/lib/sentry.ts.
const sentryDsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
const sentryAllowed = import.meta.env.PROD || import.meta.env.VITE_SENTRY_ALLOW_NON_PRODUCTION === 'true';
if (sentryDsn && sentryAllowed) {
  Sentry.init({
    dsn: sentryDsn,
    environment: import.meta.env.MODE,
    tracesSampleRate: 0.1,
  });
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 30_000, retry: 1 },
  },
});

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <Sentry.ErrorBoundary fallback={<div style={{ padding: 24 }}>Something went wrong. Refresh the page.</div>}>
      {/*
        Relative redirect URLs keep users on the origin they signed in from.
        Demo (demo.airplanepartsdirect.com) shares the same Clerk production
        instance as prod, whose dashboard Home URL is the absolute prod app URL.
        Without these, Clerk falls back to that absolute URL and bounces demo
        logins to app.airplanepartsdirect.com. Relative "/" resolves per-origin.
      */}
      <ClerkProvider
        publishableKey={publishableKey}
        signInUrl="/sign-in"
        signInFallbackRedirectUrl="/"
        signUpFallbackRedirectUrl="/"
      >
        <QueryClientProvider client={queryClient}>
          <CopilotProvider>
            <App />
          </CopilotProvider>
        </QueryClientProvider>
      </ClerkProvider>
    </Sentry.ErrorBoundary>
  </React.StrictMode>,
);
